import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { Column } from 'components/UI/Column';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { customersPrefix } from 'routes/routes';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
/* import { PageIndex } from 'components/UI/PageIndex'; */
import { TableLoader } from 'components/Loaders/TableLoader';
import { Dialog, DialogActions, DialogTitle } from '@material-ui/core';
import { resetCustomerForm } from '../CustomerDetails/customerForm';
import {
  closeError,
  deleteCustomer,
  fetchCustomers,
  resetCustomers,
  getCustomers,
  getCustomersLoaders,
  getCustomersApiErrors
  /* getCustomersPagination */
} from './customers';

export const CustomersList: FC<{}> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const customers = useSelector(getCustomers);
  /* const pagination = useSelector(getCustomersPagination); */
  /* const [selectedIndex, setSelectedIndex] = useState<any>(pagination.index); */
  const { isLoading, isDeletingItems } = useSelector(getCustomersLoaders);
  const { loadingError, deletingError } = useSelector(getCustomersApiErrors);
  const tableRef = useRef<HTMLTableElement>(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState<string | undefined>(undefined);

  useEffect(() => {
    dispatch(resetCustomerForm());
    dispatch(fetchCustomers(null));
    return () => {
      dispatch(resetCustomers());
    };
  }, []);

  const cancelDeleteCustomer = () => {
    setDeleteCustomerId(undefined);
  };

  const confirmDelete = () => {
    if (deleteCustomerId) {
      dispatch(deleteCustomer(deleteCustomerId));
    }
    cancelDeleteCustomer();
  };

  const askDeleteCustomer = (id: string) => {
    setDeleteCustomerId(id);
  };

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  // TODO: FOR THIS KIND OF EMPTY LIST MAKE HOOK
  if (!isLoading && isEmpty(customers)) {
    return (
      <Column justifyContent="center" alignItems="center">
        <Typography style={{ marginBottom: '25px' }}>{t('customers.not found')}</Typography>
        <Button variant="contained" color="primary" onClick={() => history.push(`${customersPrefix}/new`)}>
          {t('customers.new customer')}
        </Button>
      </Column>
    );
  }

  return (
    <>
      <div>
        <Button
          variant="contained"
          color="primary"
          component="span"
          onClick={() => history.push(`${customersPrefix}/new`)}
        >
          {t('customers.new customer')}
        </Button>
      </div>
      <div style={{ height: '20px' }} />
      <TableContainer component={Paper} elevation={3} ref={tableRef}>
        <Table stickyHeader aria-label="order list">
          <TableHead>
            <TableRow>
              <TableCell>{t('label.company name')}</TableCell>
              <TableCell>{t('label.email')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableLoader colSpan={3} />}
            {!isLoading && (
              <>
                {map(customers, (customer: any, index: number) => (
                  <TableRow key={`user_${index}`}>
                    <TableCell>{customer.company_name}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="edit"
                        color="primary"
                        onClick={() => history.push(`${customersPrefix}/details/${customer.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled={isDeletingItems && isDeletingItems[customer.id]}
                        onClick={() => {
                          askDeleteCustomer(customer.id);
                        }}
                      >
                        <DeleteIcon />
                        {isDeletingItems && isDeletingItems[customer.id] && (
                          <CircularProgress
                            color="secondary"
                            size={34}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: -17,
                              marginLeft: -17
                            }}
                          />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* <PageIndex
        margin="25px 0"
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={async (_, page: number) => {
          await dispatch(fetchCustomers({ index: page }));
          if (tableRef) {
            tableRef?.current?.scrollIntoView();
          }
        }}
      /> */}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(deletingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {deletingError}
        </Alert>
      </Snackbar>

      <Dialog open={deleteCustomerId !== undefined} onClose={() => setDeleteCustomerId(undefined)}>
        <DialogTitle>{t('customers.confirm delete')}</DialogTitle>
        <DialogActions>
          <Button color="primary" onClick={confirmDelete}>
            {t('common.ok')}
          </Button>
          <Button color="secondary" onClick={cancelDeleteCustomer}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
